import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Volume2, VolumeX, Loader2, Users, FileText, MessageCircle, Shield, Sparkles, ClipboardList, Pen } from 'lucide-react';
import { toast } from 'sonner';
import { markSlideViewed, getActivityProgress } from '../utils/progressTracker';

const ACTIVITY_ID = 'deeje-dc3';

const theme = {
  primary: '#457B9D',
  secondary: '#E76F51',
  bg: '#F5F9FC',
  text: '#2C3E50',
};

const bgImage = "https://customer-assets.emergentagent.com/job_9e595d24-b8ef-429a-8a8c-c23583fe1376/artifacts/uwszpqa9_image.png";

const slides = [
  {
    id: 1, type: 'title',
    title: "DC 3 – Communication professionnelle",
    bloc5: "Bloc de compétences n° 5 : Expression et communication écrite et orale",
    bloc6: "Bloc de compétences n° 6 : Communication professionnelle en travail social"
  },
  {
    id: 2, type: 'sommaire',
    title: "Sommaire",
    sections: [
      { num: "1", title: "3.1 Contribuer à l'élaboration et à la conduite du projet social, éducatif et pédagogique du service, de l'établissement, de l'institution en coopération avec la famille" },
      { num: "2", title: "3.2 Organiser et mener des réunions" },
      { num: "3", title: "3.3 Communiquer avec les familles" },
      { num: "4", title: "3.4 Créer des supports de communication adaptés aux destinataires" },
      { num: "5", title: "3.5 Rédiger des écrits professionnels" },
      { num: "6", title: "3.6 S'inscrire dans un travail d'équipe" },
      { num: "7", title: "3.7 Mener des actions d'information et de sensibilisation en direction des familles, des enfants et des partenaires" },
      { num: "8", title: "3.8 Appréhender et mobiliser l'environnement numérique" },
    ]
  },
  // INTRODUCTION
  { id: 3, type: 'section', section: "1", title: "Introduction au DC3" },
  {
    id: 4, type: 'content',
    title: "Travailler en équipe : un acte professionnel",
    content: "Le DC3 interroge la capacité du professionnel à s'inscrire dans une équipe pluriprofessionnelle, à communiquer efficacement et à contribuer à la cohérence de l'action éducative. Le travail d'équipe n'est pas une simple cohabitation ; il est une construction permanente fondée sur le dialogue, la reconnaissance mutuelle et un projet partagé.",
    bullets: [
      "Différencier les projets (établissement, social, éducatif, pédagogique)",
      "Contribuer activement à leur élaboration et mise en œuvre",
      "Maîtriser les écrits professionnels",
      "Organiser et animer des réunions",
      "Communiquer avec clarté et rigueur"
    ]
  },
  // CADRE JURIDIQUE
  { id: 5, type: 'section', section: "2", title: "Cadre juridique du DC3" },
  {
    id: 6, type: 'content',
    title: "Fondements réglementaires de la communication",
    content: "La communication professionnelle en travail social s'inscrit dans un cadre strict qui garantit la qualité des échanges et la protection des informations.",
    bullets: [
      "Secret professionnel et partage d'informations (Code pénal, Art. 226-13)",
      "Droit à l'information des familles (CASF)",
      "RGPD et protection des données personnelles",
      "Obligation de signalement (protection de l'enfance)",
      "Règlement intérieur et projet d'établissement",
      "Cadre déontologique des professions sociales"
    ]
  },
  // 3.1 CONTRIBUER À L'ÉLABORATION DU PROJET
  { id: 7, type: 'section', title: "3.1 – Contribuer à l'élaboration et à la conduite du projet social, éducatif et pédagogique du service, de l'établissement ou de l'institution en coopération avec la famille" },
  {
    id: 8, type: 'content',
    title: "Les différents niveaux de projet",
    content: "Un projet n'est pas un document figé. C'est un processus dynamique d'élaboration collective ancré dans une réalité institutionnelle et territoriale. L'EJE doit comprendre l'articulation entre les différents niveaux de projet.",
    bullets: [
      "Projet d'établissement : cadre général, missions, valeurs",
      "Projet social : insertion dans le territoire, partenariats",
      "Projet éducatif : orientations éducatives, principes pédagogiques",
      "Projet pédagogique : traduction concrète des orientations en actions",
      "Projet d'accompagnement individualisé : adaptation à chaque enfant"
    ],
    note: "Chaque niveau de projet s'emboîte dans le précédent. La cohérence entre ces niveaux est essentielle."
  },
  {
    id: 9, type: 'content',
    title: "Contribuer au projet : une démarche globale",
    content: "Contribuer à l'élaboration d'un projet social, éducatif et pédagogique suppose une compréhension globale des enjeux institutionnels et territoriaux. Le projet constitue le cadre structurant de l'action professionnelle. Il donne du sens aux pratiques et assure leur cohérence. Le professionnel ne se contente pas d'appliquer un projet existant ; il participe activement à sa construction, à son évolution et à sa mise en œuvre.",
    content2: "Cette contribution s'inscrit dans une dynamique de coopération avec l'équipe et les familles. Le projet n'est pas un document figé, mais un outil vivant au service du développement du jeune enfant et de la qualité de l'accompagnement."
  },
  {
    id: 10, type: 'content',
    title: "Se situer dans un service, une institution et sur un territoire",
    content: "Contribuer au projet nécessite de connaître l'organisation interne de la structure, ses missions et son fonctionnement. Le professionnel identifie sa place, ses responsabilités et ses marges d'initiative.",
    content2: "Il prend également en compte l'environnement territorial. La structure s'inscrit dans un réseau d'acteurs et dans des politiques publiques locales. Cette dimension territoriale influence les priorités et les ressources mobilisables.",
    content3: "Se situer suppose donc une analyse du contexte institutionnel et social."
  },
  {
    id: 11, type: 'content',
    title: "Prendre en compte l'expression des familles",
    content: "La coopération avec les familles constitue un principe fondamental. Le projet ne peut être élaboré sans tenir compte des besoins, des attentes et des préoccupations parentales.",
    content2: "Recueillir l'expression des familles passe par des échanges formels et informels. Les réunions, les questionnaires ou les temps d'échange participent à cette prise en compte.",
    content3: "Intégrer la parole des parents renforce la pertinence du projet et favorise l'adhésion."
  },
  {
    id: 12, type: 'content',
    title: "Repérer les critères de faisabilité et de pertinence",
    content: "Toute proposition doit être analysée au regard de sa pertinence et de sa faisabilité. La pertinence repose sur l'adéquation entre les objectifs du projet et les besoins identifiés. La faisabilité dépend des ressources humaines, matérielles et financières disponibles.",
    content2: "Le professionnel évalue ces critères avant de s'engager dans une action. Cette analyse prévient les projets irréalistes et favorise une gestion responsable."
  },
  {
    id: 13, type: 'content',
    title: "Inscrire le projet dans un cadre réglementaire et financier",
    content: "Les projets éducatifs et sociaux s'inscrivent dans un cadre réglementaire précis. Le professionnel doit connaître les obligations légales, les normes en vigueur et les orientations institutionnelles.",
    content2: "La dimension financière ne peut être ignorée. Les choix pédagogiques doivent être compatibles avec le budget alloué. Cette contrainte nécessite rigueur et anticipation.",
    content3: "Inscrire le projet dans ces cadres garantit sa légitimité et sa durabilité."
  },
  {
    id: 14, type: 'content',
    title: "Participer à la définition des orientations et des objectifs",
    content: "Le professionnel contribue activement à la définition des orientations stratégiques. Il apporte son expertise issue de l'observation du terrain et de l'analyse des besoins.",
    content2: "La formulation d'objectifs clairs et mesurables permet de structurer l'action. Ces objectifs orientent les pratiques quotidiennes et facilitent l'évaluation.",
    content3: "La participation à cette définition renforce l'engagement et la cohérence de l'équipe."
  },
  {
    id: 15, type: 'content',
    title: "Argumenter ses propositions",
    content: "La contribution au projet suppose une capacité d'argumentation. Les propositions doivent être justifiées par des observations, des références théoriques et une analyse contextualisée.",
    content2: "Argumenter signifie exposer les enjeux, les bénéfices attendus et les conditions de réussite. Cette compétence favorise un dialogue constructif au sein de l'équipe et avec les partenaires."
  },
  {
    id: 16, type: 'content',
    title: "Participer à l'élaboration et à la conduite des projets",
    content: "La participation ne se limite pas à la phase de conception. Elle inclut la mise en œuvre et le suivi des actions. Le professionnel s'implique dans l'organisation, la coordination et l'évaluation.",
    content2: "La conduite du projet demande organisation, rigueur et capacité d'adaptation. Les ajustements sont nécessaires en fonction des imprévus ou de l'évolution des besoins."
  },
  {
    id: 17, type: 'content',
    title: "Utiliser la méthodologie de projet",
    content: "La méthodologie de projet constitue un outil structurant. Elle comprend l'analyse des besoins, la définition des objectifs, la planification des actions, la mobilisation des ressources et l'évaluation.",
    content2: "Cette démarche garantit une action réfléchie et cohérente. Elle favorise la participation de l'équipe et renforce la qualité des interventions."
  },
  {
    id: 18, type: 'content',
    title: "Mutualiser et transmettre ses connaissances",
    content: "Le projet s'enrichit par la mutualisation des savoirs. Partager les expériences, les compétences et les analyses favorise une dynamique collective.",
    content2: "La transmission peut se faire lors de réunions, de formations internes ou d'échanges de pratiques. Elle contribue à la professionnalisation de l'équipe et à l'évolution du projet."
  },
  {
    id: 19, type: 'content',
    title: "Synthèse",
    content: "Contribuer à l'élaboration et à la conduite du projet social, éducatif et pédagogique exige une posture réflexive, collaborative et structurée. Le professionnel articule analyse, communication et méthodologie pour inscrire son action dans une dynamique institutionnelle cohérente.",
    content2: "En coopérant avec les familles et l'équipe, il participe activement à la construction d'un projet vivant, porteur de sens et adapté aux besoins du jeune enfant et de son environnement."
  },
  // 3.2 ORGANISER ET MENER DES RÉUNIONS
  { id: 20, type: 'section', title: "3.2 – Organiser et mener des réunions" },
  {
    id: 21, type: 'content',
    title: "La réunion : un outil de coordination essentiel",
    content: "Les réunions sont des espaces de coordination, de décision et de réflexion. Leur efficacité repose sur une préparation rigoureuse, une animation structurée et un suivi des décisions prises.",
    bullets: [
      "Définir un objectif clair pour chaque réunion",
      "Préparer un ordre du jour et le diffuser en amont",
      "Animer en favorisant la parole de chacun",
      "Gérer le temps et recentrer les échanges si nécessaire",
      "Rédiger un compte rendu avec les décisions et actions prévues",
      "Assurer le suivi des engagements pris"
    ]
  },
  // 3.2 CONTENU
  {
    id: 22, type: 'content',
    title: "Organiser et mener des réunions",
    content: "La réunion constitue un outil central de la communication professionnelle en travail social. Elle permet la circulation des informations, la réflexion collective, la prise de décision et la coordination des actions. Organiser et mener une réunion requiert des compétences méthodologiques et relationnelles. Une réunion efficace ne s'improvise pas : elle se prépare, se structure et s'évalue.",
    content2: "Dans le champ de la petite enfance, les réunions peuvent prendre différentes formes : réunions d'équipe, réunions de projet, rencontres avec les partenaires ou temps d'échanges avec les familles. Leur qualité conditionne la cohérence des pratiques et la dynamique collective."
  },
  {
    id: 23, type: 'content',
    title: "Définir des objectifs de réunion",
    content: "Toute réunion doit répondre à un objectif précis. Il peut s'agir d'informer, de réfléchir, de décider, de résoudre une problématique ou d'évaluer une action. L'absence d'objectif clair entraîne dispersion et inefficacité.",
    content2: "La définition des objectifs permet de structurer l'ordre du jour et d'identifier les participants nécessaires. Elle facilite également la gestion du temps et la priorisation des sujets abordés.",
    content3: "Un objectif explicite favorise l'engagement des participants. Chacun comprend le sens de sa présence et les résultats attendus."
  },
  {
    id: 24, type: 'content',
    title: "Organiser des espaces de réunion",
    content: "L'organisation matérielle influence la qualité des échanges. Un espace calme, adapté au nombre de participants et propice à la concentration favorise la participation. La disposition des sièges peut soutenir la dynamique relationnelle. Une configuration circulaire, par exemple, encourage l'égalité de parole.",
    content2: "La préparation logistique inclut la diffusion de l'ordre du jour, la mise à disposition des documents nécessaires et la planification d'une durée adaptée aux objectifs.",
    content3: "L'organisation du temps constitue également un facteur clé. Prévoir des séquences structurées et respecter les horaires renforce le professionnalisme et la crédibilité de l'animateur."
  },
  {
    id: 25, type: 'content',
    title: "Animer des réunions",
    content: "L'animation demande des compétences spécifiques. L'animateur veille à la clarté des échanges, à la régulation des prises de parole et au respect du cadre fixé. Il favorise la participation de chacun tout en évitant les digressions excessives.",
    content2: "L'écoute active, la reformulation et la synthèse intermédiaire permettent de maintenir la cohérence des discussions. L'animateur doit également gérer les tensions éventuelles et favoriser un climat respectueux.",
    content3: "La conclusion de la réunion est essentielle. Elle doit récapituler les décisions prises, préciser les responsabilités et fixer les échéances. Un compte rendu formalisé assure la traçabilité et la continuité."
  },
  {
    id: 26, type: 'content',
    title: "Mobiliser un collectif d'acteurs concernés",
    content: "Une réunion pertinente implique la présence des acteurs concernés par le sujet abordé. Identifier les participants adéquats garantit la qualité des échanges et la légitimité des décisions.",
    content2: "Mobiliser un collectif suppose de valoriser la contribution de chacun. Le professionnel encourage l'expression des différents points de vue et reconnaît la diversité des compétences présentes.",
    content3: "La mobilisation passe également par la préparation en amont. Informer les participants des objectifs et des attentes favorise leur engagement."
  },
  {
    id: 27, type: 'content',
    title: "Synthèse",
    content: "Organiser et mener une réunion exige méthode, anticipation et compétences relationnelles. La réunion devient un espace structuré de réflexion et de décision au service du projet éducatif. En définissant des objectifs clairs, en organisant un cadre adapté, en animant avec rigueur et en mobilisant les acteurs concernés, le professionnel contribue à renforcer la cohésion d'équipe et la qualité des actions menées auprès du jeune enfant et de sa famille."
  },
  {
    id: 28, type: 'competence',
    title: "Compétences clés - Réunions et communication",
    items: [
      "Définir des objectifs de réunion",
      "Organiser et animer des réunions",
      "Créer des supports de communication adaptés",
      "Utiliser des techniques d'animation participatives",
      "Rédiger des comptes rendus structurés"
    ]
  },
  // 3.3 COMMUNIQUER AVEC LES FAMILLES
  { id: 29, type: 'section', title: "3.3 – Communiquer avec les familles" },
  {
    id: 30, type: 'content',
    title: "Communiquer avec les familles",
    content: "La communication avec les familles constitue un axe fondamental de la pratique professionnelle en petite enfance. Elle ne se limite pas à transmettre des informations ; elle participe à la construction d'un partenariat éducatif fondé sur la confiance, la reconnaissance mutuelle et la coopération. La qualité de la communication influence directement la sécurité affective de l'enfant et la cohérence des actions éducatives.",
    content2: "Communiquer avec les familles nécessite une posture professionnelle claire, une capacité d'adaptation et une maîtrise des différents supports de communication."
  },
  {
    id: 31, type: 'content',
    title: "Organiser des temps de rencontre avec les familles",
    content: "Les temps de rencontre peuvent être formels ou informels. Les transmissions quotidiennes constituent un premier niveau d'échange. Elles permettent de partager les informations essentielles concernant la journée de l'enfant et de maintenir un lien régulier.",
    content2: "Des rencontres plus structurées, telles que les entretiens individuels, les réunions collectives ou les temps thématiques, offrent un espace d'échange approfondi. Ces moments permettent d'aborder le développement de l'enfant, les projets pédagogiques ou les questionnements parentaux.",
    content3: "L'organisation de ces temps suppose anticipation et clarté. Les objectifs doivent être définis, le cadre posé et la confidentialité respectée."
  },
  {
    id: 32, type: 'content',
    title: "Prendre en compte les propositions des familles",
    content: "La communication ne peut être unidirectionnelle. Les familles doivent pouvoir exprimer leurs idées, leurs attentes et leurs propositions. Prendre en compte ces contributions signifie les écouter réellement et les intégrer, lorsque cela est possible, dans le fonctionnement de la structure.",
    content2: "Cette prise en compte renforce le sentiment de reconnaissance des parents. Elle favorise leur implication et consolide le partenariat éducatif.",
    content3: "Lorsque certaines propositions ne peuvent être retenues, le professionnel explique les raisons avec transparence, en s'appuyant sur le cadre institutionnel."
  },
  {
    id: 33, type: 'content',
    title: "Faciliter l'expression des besoins et des attentes",
    content: "Certaines familles peuvent hésiter à exprimer leurs besoins par crainte du jugement ou par manque de confiance. Le professionnel crée un climat sécurisant favorisant la parole.",
    content2: "L'écoute active, la reformulation et l'absence de jugement facilitent cette expression. Des supports tels que des questionnaires ou des temps de parole collectifs peuvent également être utilisés pour recueillir les attentes.",
    content3: "La capacité à accueillir les émotions et les inquiétudes parentales constitue un élément central de cette communication."
  },
  {
    id: 34, type: 'content',
    title: "Valoriser les propositions des familles",
    content: "Valoriser les propositions signifie reconnaître les compétences parentales et souligner leur contribution au projet éducatif. Cette valorisation peut se traduire par l'intégration d'idées dans des projets collectifs ou par la mise en avant des savoir-faire familiaux.",
    content2: "La reconnaissance des initiatives parentales renforce la confiance et soutient la fonction parentale. Elle contribue également à créer une dynamique collaborative."
  },
  {
    id: 35, type: 'content',
    title: "Adapter son mode d'expression et de communication",
    content: "La communication doit être adaptée au contexte et aux interlocuteurs. Le professionnel ajuste son langage en fonction du niveau de compréhension, des références culturelles et de la situation.",
    content2: "Un vocabulaire clair, accessible et dépourvu de jargon professionnel favorise la compréhension. La posture non verbale, le ton de voix et l'attitude corporelle participent également à la qualité de l'échange.",
    content3: "L'adaptation inclut la gestion des situations sensibles. Les annonces délicates nécessitent un cadre approprié et une communication mesurée."
  },
  {
    id: 36, type: 'content',
    title: "Savoir communiquer en langue étrangère",
    content: "Dans un contexte de diversité culturelle, la capacité à communiquer en langue étrangère constitue un atout. Lorsque cela n'est pas possible, des supports visuels, des pictogrammes ou des outils de traduction peuvent être utilisés.",
    content2: "L'objectif est de garantir l'accès à l'information et d'éviter les malentendus. La communication interculturelle implique également une ouverture aux différences de représentations éducatives et de modes de vie."
  },
  {
    id: 37, type: 'content',
    title: "Utiliser les outils bureautiques et numériques",
    content: "Les outils numériques facilitent la communication avec les familles. Les courriels, les plateformes sécurisées ou les applications dédiées permettent de transmettre des informations, des documents ou des photos dans le respect du cadre réglementaire.",
    content2: "L'utilisation de ces outils nécessite vigilance et respect de la confidentialité. Les données personnelles doivent être protégées conformément aux règles en vigueur.",
    content3: "Les supports numériques complètent les échanges en présentiel sans les remplacer. La relation directe demeure essentielle."
  },
  {
    id: 38, type: 'content',
    title: "Synthèse",
    content: "Communiquer avec les familles demande professionnalisme, adaptabilité et sens relationnel. En organisant des temps de rencontre, en valorisant la parole parentale et en utilisant des outils adaptés, le professionnel construit un partenariat solide. Cette communication contribue à la cohérence éducative et au bien-être du jeune enfant, inscrit dans un environnement familial et institutionnel en dialogue constant."
  },
  // 3.4 CRÉER DES SUPPORTS DE COMMUNICATION
  { id: 39, type: 'section', title: "3.4 – Créer des supports de communication adaptés aux destinataires" },
  {
    id: 40, type: 'content',
    title: "Créer des supports de communication adaptés aux destinataires",
    content: "La communication professionnelle ne se limite pas à l'échange oral. Elle s'appuie sur des supports variés permettant de transmettre des informations, de valoriser des projets et de renforcer l'engagement des acteurs concernés. Créer des supports de communication adaptés suppose d'identifier précisément les destinataires, leurs besoins, leurs attentes et leurs modes d'accès à l'information.",
    content2: "Un support efficace est clair, accessible, cohérent avec les valeurs institutionnelles et adapté au contexte territorial. Il doit permettre la compréhension, l'adhésion et la participation."
  },
  {
    id: 41, type: 'content',
    title: "Créer les conditions permettant aux acteurs de s'impliquer",
    content: "La communication autour des projets ne peut être descendante. Elle doit favoriser l'implication des familles, des professionnels et des partenaires. Créer les conditions de cette implication implique de rendre les projets lisibles et compréhensibles.",
    content2: "Un projet explicité, illustré et contextualisé facilite l'adhésion. Les supports peuvent présenter les objectifs, les étapes, les modalités de participation et les bénéfices attendus.",
    content3: "La transparence renforce la confiance. Lorsque les acteurs comprennent le sens d'un projet, ils sont davantage enclins à s'y engager."
  },
  {
    id: 42, type: 'content',
    title: "Promouvoir le projet social, éducatif et culturel sur le territoire",
    content: "La structure d'accueil s'inscrit dans un territoire et participe à sa dynamique sociale et culturelle. Promouvoir le projet institutionnel permet de valoriser les actions menées et de renforcer les partenariats.",
    content2: "Cette promotion peut passer par des affiches, des plaquettes de présentation, des newsletters ou des publications numériques. Elle vise à rendre visible l'engagement éducatif et social de la structure.",
    content3: "La communication territoriale doit rester fidèle aux valeurs portées par le projet. Elle contribue à la reconnaissance institutionnelle et à la création de liens avec les acteurs locaux."
  },
  {
    id: 43, type: 'content',
    title: "Utiliser des outils de création de supports",
    content: "La conception de supports de communication nécessite une maîtrise des outils bureautiques et numériques. Les logiciels de traitement de texte, de présentation ou de mise en page permettent de produire des documents structurés et professionnels.",
    content2: "La clarté visuelle joue un rôle important. L'utilisation de titres explicites, d'illustrations adaptées et d'une mise en page aérée facilite la lecture. Les supports doivent être accessibles à tous, en tenant compte des éventuelles difficultés linguistiques ou de compréhension.",
    content3: "La cohérence graphique renforce l'identité de la structure. Elle participe à la lisibilité et à la crédibilité des messages transmis."
  },
  {
    id: 44, type: 'content',
    title: "Utiliser différentes techniques et supports de communication",
    content: "La diversité des publics nécessite l'utilisation de supports variés. L'affichage mural permet une information immédiate et visible. Les supports numériques facilitent la diffusion rapide et personnalisée. Les documents imprimés peuvent être remis directement aux familles.",
    content2: "La communication orale, lors de réunions ou de temps d'échange, complète ces supports écrits. Les vidéos, photographies ou supports visuels enrichissent également la transmission des informations.",
    content3: "L'adaptation des techniques dépend du message à transmettre. Une information pratique nécessite un support clair et synthétique, tandis qu'un projet éducatif peut faire l'objet d'une présentation plus détaillée."
  },
  {
    id: 45, type: 'content',
    title: "Synthèse",
    content: "Créer des supports de communication adaptés demande réflexion, méthodologie et créativité. Le professionnel identifie les destinataires, choisit les outils appropriés et veille à la cohérence des messages. En favorisant l'implication des acteurs et en valorisant le projet institutionnel, il contribue à renforcer la visibilité, la compréhension et l'adhésion autour des actions éducatives et sociales menées sur le territoire."
  },
  // 3.5 RÉDIGER DES ÉCRITS PROFESSIONNELS
  { id: 46, type: 'section', title: "3.5 – Rédiger des écrits professionnels" },
  {
    id: 47, type: 'content',
    title: "Rédiger des écrits professionnels",
    content: "L'écrit professionnel constitue un outil central de la communication en travail social et en petite enfance. Il assure la traçabilité des actions, la continuité des accompagnements et la coordination entre les acteurs. Rédiger ne consiste pas seulement à relater des faits ; il s'agit de produire un document structuré, pertinent et conforme aux exigences éthiques et réglementaires.",
    content2: "L'écrit engage la responsabilité du professionnel. Il doit être précis, objectif et respectueux de la confidentialité. Il participe à la qualité de l'accompagnement et à la crédibilité institutionnelle."
  },
  {
    id: 48, type: 'content',
    title: "Écrire : un acte professionnel engagé",
    content: "Les écrits professionnels sont des outils de communication, de mémoire et de protection. Ils engagent la responsabilité du professionnel et contribuent à la qualité du suivi éducatif.",
    bullets: [
      "Transmissions quotidiennes : informations sur l'enfant et sa journée",
      "Observations écrites : descriptions factuelles et analyses",
      "Comptes rendus de réunion : décisions et actions",
      "Projets d'accompagnement : objectifs et moyens",
      "Rapports d'activité : bilan et perspectives",
      "Signalements : éléments factuels dans le cadre de la protection"
    ],
    note: "Les écrits doivent être factuels, datés, signés et respecter les règles de confidentialité."
  },
  {
    id: 49, type: 'content',
    title: "Adapter l'écrit à son destinataire",
    content: "Tout écrit professionnel s'adresse à un destinataire spécifique. Le registre de langue, le niveau de détail et le format doivent être adaptés en conséquence.",
    bullets: [
      "Pour l'équipe : précision technique, vocabulaire professionnel",
      "Pour les familles : clarté, accessibilité, bienveillance",
      "Pour les partenaires : rigueur, synthèse, objectivité",
      "Pour les instances : structuration, argumentation, références",
      "Toujours distinguer les faits des interprétations"
    ]
  },
  {
    id: 50, type: 'content',
    title: "Identifier les différents types d'écrits et les élaborer",
    content: "Les écrits professionnels peuvent prendre différentes formes selon les contextes et les objectifs. Il peut s'agir de comptes rendus de réunion, de notes d'observation, de transmissions écrites, de bilans éducatifs, de projets pédagogiques ou de rapports destinés à des partenaires institutionnels.",
    content2: "Chaque type d'écrit possède ses spécificités. Une note d'observation privilégie la description factuelle et contextualisée. Un compte rendu synthétise les échanges et précise les décisions prises. Un projet pédagogique expose des objectifs, des moyens et des modalités d'évaluation.",
    content3: "Identifier le type d'écrit attendu permet d'adopter une structure adaptée et un niveau de détail pertinent."
  },
  {
    id: 51, type: 'content',
    title: "Identifier, ordonnancer et hiérarchiser les informations",
    content: "Un écrit efficace repose sur une organisation claire des informations. Le professionnel sélectionne les éléments pertinents en lien avec l'objectif visé. Il distingue les faits observables des interprétations.",
    content2: "L'ordonnancement logique facilite la compréhension. Une introduction contextualise la situation, le développement présente les éléments essentiels et la conclusion peut préciser les suites envisagées.",
    content3: "La hiérarchisation permet d'éviter la dispersion. Les informations prioritaires doivent apparaître de manière explicite, tandis que les détails secondaires sont intégrés de manière mesurée."
  },
  {
    id: 52, type: 'content',
    title: "Mettre en adéquation le type d'écrit, l'objectif et le destinataire",
    content: "L'adaptation au destinataire constitue un principe fondamental. Un écrit destiné à l'équipe interne peut comporter un vocabulaire technique et des références professionnelles. Un document adressé aux familles doit être formulé dans un langage accessible et clair.",
    content2: "L'objectif influence également la rédaction. Informer, analyser, alerter ou évaluer impliquent des tonalités et des structures différentes.",
    content3: "La cohérence entre le type d'écrit, l'objectif poursuivi et le public concerné garantit la pertinence du document produit."
  },
  {
    id: 53, type: 'content',
    title: "Rédiger dans le respect des règles de partage d'informations",
    content: "Les écrits professionnels sont soumis à des règles strictes de confidentialité. Le respect du secret professionnel et des dispositions relatives à la protection des données personnelles constitue une obligation.",
    content2: "Les informations transmises doivent être nécessaires et proportionnées. Les formulations doivent rester neutres, sans jugement ni stigmatisation.",
    content3: "La clarté, la précision et la rigueur renforcent la qualité de l'écrit. L'utilisation d'un vocabulaire professionnel approprié, la correction grammaticale et la structuration du texte participent à la crédibilité du document."
  },
  {
    id: 54, type: 'content',
    title: "Synthèse",
    content: "Rédiger des écrits professionnels exige méthode, discernement et responsabilité. L'écrit devient un outil de coordination, de réflexion et de transmission. En identifiant le type d'écrit approprié, en structurant les informations et en respectant les règles de partage, le professionnel contribue à la qualité de l'accompagnement et à la cohérence institutionnelle."
  },
  // 3.6 S'INSCRIRE DANS UN TRAVAIL D'ÉQUIPE
  { id: 55, type: 'section', title: "3.6 – S'inscrire dans un travail d'équipe" },
  {
    id: 56, type: 'content',
    title: "S'inscrire dans un travail d'équipe",
    content: "Le travail en équipe constitue le socle de l'action éducative en petite enfance. Aucun professionnel n'agit seul. L'accompagnement du jeune enfant et de sa famille s'inscrit dans une dynamique collective où les compétences se complètent et s'articulent. S'inscrire dans un travail d'équipe implique une posture professionnelle fondée sur la coopération, la communication et la réflexion partagée.",
    content2: "Le travail d'équipe ne se réduit pas à une juxtaposition d'interventions individuelles. Il repose sur une cohérence des pratiques et sur une compréhension commune des objectifs poursuivis."
  },
  {
    id: 57, type: 'content',
    title: "S'inscrire dans l'équipe pluriprofessionnelle",
    content: "L'équipe pluriprofessionnelle réunit des compétences complémentaires au service du jeune enfant. L'EJE identifie sa place, articule ses interventions avec celles des autres et contribue à la cohérence collective.",
    bullets: [
      "Identifier sa place et ses limites dans l'équipe",
      "Articuler ses interventions avec celles des collègues",
      "Contribuer à la cohérence de l'action éducative",
      "Mener des actions d'information et de sensibilisation",
      "Appréhender et mobiliser l'environnement numérique",
      "Accepter la confrontation des points de vue"
    ]
  },
  {
    id: 58, type: 'content',
    title: "Identifier sa place dans l'équipe et articuler ses interventions",
    content: "Chaque professionnel occupe une place spécifique au sein de l'équipe. Identifier cette place suppose de connaître ses missions, ses responsabilités et ses limites d'intervention. Cette clarification évite les chevauchements de rôle et favorise une articulation fluide des actions.",
    content2: "L'Éducateur de Jeunes Enfants, par exemple, peut avoir une fonction de référent pédagogique, tandis que d'autres professionnels assurent un accompagnement plus centré sur les soins ou l'organisation matérielle. L'articulation des interventions permet de répondre de manière globale aux besoins des enfants.",
    content3: "Se situer dans l'équipe implique également d'adapter sa posture en fonction des situations et des interlocuteurs."
  },
  {
    id: 59, type: 'content',
    title: "Partager et confronter ses observations et ses analyses",
    content: "Le partage des observations constitue un élément central du travail en équipe. Les regards croisés enrichissent la compréhension des situations. Un comportement perçu comme isolé par un professionnel peut prendre un sens différent lorsqu'il est analysé collectivement.",
    content2: "La confrontation constructive des analyses permet d'éviter les interprétations hâtives et de construire des réponses adaptées. Cette démarche nécessite écoute, respect et capacité à accepter la diversité des points de vue.",
    content3: "Les temps de réunion d'équipe constituent des espaces privilégiés pour ces échanges."
  },
  {
    id: 60, type: 'content',
    title: "La communication au sein de l'équipe",
    content: "La qualité de la communication interne conditionne la qualité de l'accompagnement. Elle repose sur des outils formalisés, des temps d'échange réguliers et une posture d'écoute mutuelle.",
    bullets: [
      "Les transmissions orales : moments clés de la journée",
      "Les cahiers de liaison et outils numériques partagés",
      "Les temps d'analyse de pratiques : prise de recul collective",
      "La gestion constructive des désaccords",
      "Le soutien mutuel face aux situations difficiles"
    ]
  },
  {
    id: 61, type: 'content',
    title: "Identifier les compétences de chacun",
    content: "Une équipe est composée de professionnels aux parcours et aux expertises variés. Identifier les compétences spécifiques de chacun permet de mobiliser les ressources de manière pertinente.",
    content2: "La reconnaissance des compétences renforce la confiance et la cohésion. Elle favorise également la répartition équilibrée des responsabilités et des initiatives.",
    content3: "Valoriser les savoir-faire de chacun contribue à créer un climat de travail positif et motivant."
  },
  {
    id: 62, type: 'content',
    title: "Contribuer à la cohérence de l'action éducative",
    content: "La cohérence éducative repose sur l'alignement des pratiques avec le projet institutionnel. Chaque membre de l'équipe participe à la mise en œuvre des orientations définies collectivement.",
    content2: "Cette cohérence nécessite une communication régulière et une vigilance partagée. Les ajustements sont discutés en équipe afin de maintenir une ligne commune, même lorsque les missions évoluent.",
    content3: "La capacité d'adaptation aux changements institutionnels fait partie intégrante du travail d'équipe."
  },
  {
    id: 63, type: 'content',
    title: "Favoriser une dynamique de travail collective",
    content: "La dynamique d'équipe repose sur la confiance, la reconnaissance et l'engagement. Le professionnel contribue à cette dynamique en adoptant une attitude collaborative et respectueuse.",
    content2: "Encourager l'expression, soutenir les initiatives et participer activement aux projets collectifs renforcent la motivation de l'ensemble des membres.",
    content3: "La gestion des tensions fait également partie de cette dynamique. Le dialogue et la régulation permettent de maintenir un climat propice au travail collectif."
  },
  {
    id: 64, type: 'content',
    title: "Synthèse",
    content: "S'inscrire dans un travail d'équipe demande conscience professionnelle, ouverture et engagement. En identifiant sa place, en partageant ses analyses et en contribuant à la cohérence des actions, le professionnel participe activement à la qualité de l'accompagnement proposé aux jeunes enfants et à leurs familles. Le travail d'équipe devient ainsi un levier essentiel de professionnalisation et de cohérence institutionnelle."
  },
  {
    id: 65, type: 'competence',
    title: "Compétences clés - Équipe et communication",
    items: [
      "Identifier et élaborer les différents types d'écrits",
      "Rédiger des écrits adaptés aux destinataires",
      "Identifier sa place et articuler ses interventions",
      "Contribuer à la cohérence de l'action éducative",
      "Mener des actions d'information et sensibilisation",
      "Mobiliser l'environnement numérique"
    ]
  },
  // 3.7 MENER DES ACTIONS D'INFORMATION ET DE SENSIBILISATION
  { id: 66, type: 'section', title: "3.7 – Mener des actions d'information et de sensibilisation en direction des familles, des enfants et des partenaires" },
  {
    id: 67, type: 'content',
    title: "Mener des actions d'information et de sensibilisation",
    content: "Les actions d'information et de sensibilisation constituent un levier essentiel de la communication professionnelle. Elles permettent de transmettre des connaissances, de prévenir certaines difficultés et de favoriser l'adhésion autour des projets éducatifs. Ces actions s'adressent aux familles, aux enfants et aux partenaires institutionnels ou territoriaux.",
    content2: "Informer ne signifie pas seulement diffuser un message. Sensibiliser implique d'expliquer, de contextualiser et de susciter une réflexion. Le professionnel adopte une posture pédagogique et collaborative, en veillant à adapter son discours aux différents publics."
  },
  {
    id: 68, type: 'content',
    title: "Mobiliser les compétences des acteurs impliqués",
    content: "La réussite d'une action d'information repose sur la mobilisation des compétences de l'équipe et des partenaires. Chaque acteur peut apporter une expertise spécifique. Les professionnels de santé, les psychologues ou les intervenants culturels peuvent enrichir les contenus.",
    content2: "La coordination de ces compétences nécessite une organisation rigoureuse et une communication claire. Le professionnel veille à définir les rôles de chacun et à articuler les interventions.",
    content3: "Cette mobilisation renforce la crédibilité et la qualité des actions menées."
  },
  {
    id: 69, type: 'content',
    title: "Structurer, synthétiser et analyser l'information",
    content: "L'information transmise doit être structurée et accessible. Le professionnel analyse les données disponibles, sélectionne les éléments pertinents et les organise de manière cohérente.",
    content2: "La synthèse facilite la compréhension. Les messages doivent être clairs, précis et adaptés au niveau de connaissance des destinataires. L'utilisation d'exemples concrets et de supports visuels peut renforcer l'impact.",
    content3: "La capacité d'analyse permet également d'adapter le contenu aux besoins identifiés et d'éviter une surcharge d'informations."
  },
  {
    id: 70, type: 'content',
    title: "Sélectionner et présenter les informations utiles",
    content: "Toutes les informations ne sont pas nécessaires à tous les publics. Le professionnel identifie les données indispensables et les présente de manière hiérarchisée.",
    content2: "La présentation peut varier selon le contexte : réunion collective, atelier participatif, support écrit ou communication numérique. L'objectif reste de transmettre un message pertinent, compréhensible et mobilisateur.",
    content3: "La clarté et la cohérence renforcent l'efficacité de la communication."
  },
  {
    id: 71, type: 'content',
    title: "Orienter vers les services et les personnes compétents",
    content: "L'action d'information peut révéler des besoins spécifiques nécessitant une orientation vers des services adaptés. Le professionnel connaît les ressources disponibles sur le territoire et peut orienter les familles vers les interlocuteurs compétents.",
    content2: "Cette orientation s'effectue dans le respect du cadre réglementaire et de la confidentialité. Elle suppose une connaissance actualisée du réseau partenarial.",
    content3: "L'accompagnement dans cette démarche favorise l'accès aux droits et aux services."
  },
  {
    id: 72, type: 'content',
    title: "Communiquer sur les projets et les actions éducatives",
    content: "Informer sur les projets permet de valoriser les actions menées et de renforcer l'adhésion des familles et des partenaires. La communication autour des projets peut prendre différentes formes : réunions, affichages, supports numériques ou événements spécifiques.",
    content2: "Cette communication contribue à la visibilité institutionnelle et à la compréhension des orientations éducatives.",
    content3: "Le professionnel veille à présenter les objectifs, les modalités et les résultats attendus de manière claire et structurée."
  },
  {
    id: 73, type: 'content',
    title: "Sensibiliser aux enjeux éducatifs",
    content: "La sensibilisation vise à développer la compréhension des enjeux liés au développement du jeune enfant. Elle peut porter sur la gestion des émotions, l'importance du jeu, le sommeil ou la socialisation.",
    content2: "Le professionnel adopte une posture respectueuse, sans jugement. Il explique les fondements éducatifs et invite à la réflexion.",
    content3: "Cette sensibilisation contribue à renforcer la cohérence éducative entre la famille et la structure."
  },
  {
    id: 74, type: 'content',
    title: "Identifier, ordonnancer et hiérarchiser les informations",
    content: "La transmission efficace repose sur une organisation logique des contenus. Le professionnel hiérarchise les informations en fonction de leur importance et de leur urgence.",
    content2: "Un message structuré favorise la compréhension et l'appropriation. Il évite la dispersion et renforce la clarté."
  },
  {
    id: 75, type: 'content',
    title: "Synthèse",
    content: "Mener des actions d'information et de sensibilisation exige méthode, rigueur et compétences relationnelles. En mobilisant les ressources, en structurant les messages et en orientant les acteurs vers les services compétents, le professionnel contribue à la diffusion d'informations pertinentes et à la promotion d'une culture éducative partagée. Ces actions participent pleinement à la qualité du partenariat avec les familles et à l'inscription de la structure dans une dynamique territoriale cohérente."
  },
  // 3.8 APPRÉHENDER ET MOBILISER L'ENVIRONNEMENT NUMÉRIQUE
  { id: 76, type: 'section', title: "3.8 – Appréhender et mobiliser l'environnement numérique" },
  {
    id: 77, type: 'content',
    title: "Appréhender et mobiliser l'environnement numérique",
    content: "L'environnement numérique occupe aujourd'hui une place centrale dans la communication professionnelle et dans le fonctionnement des structures d'accueil du jeune enfant. Appréhender et mobiliser ces outils ne signifie pas seulement savoir les utiliser techniquement ; cela suppose une compréhension des enjeux éthiques, réglementaires et relationnels liés à leur usage.",
    content2: "Le numérique constitue un support facilitant l'organisation, la transmission d'informations et la valorisation des projets. Toutefois, son utilisation doit rester maîtrisée et adaptée au cadre professionnel."
  },
  {
    id: 78, type: 'content',
    title: "Utiliser les outils bureautiques et numériques",
    content: "La maîtrise des outils bureautiques permet de produire des documents professionnels structurés et de qualité. Les logiciels de traitement de texte, de tableur ou de présentation facilitent la rédaction de projets, de comptes rendus et de supports de communication.",
    content2: "Les outils numériques peuvent également soutenir la gestion administrative, le suivi des présences, la planification des activités ou la communication interne. Leur utilisation améliore l'efficacité et la traçabilité des actions menées.",
    content3: "Le professionnel veille à actualiser régulièrement ses compétences numériques afin de garantir une utilisation pertinente et sécurisée des outils disponibles."
  },
  {
    id: 79, type: 'content',
    title: "Utiliser différents modes de communication dans le respect du cadre",
    content: "La communication numérique peut prendre différentes formes : courriels, plateformes sécurisées, applications dédiées aux structures d'accueil ou supports collaboratifs. Ces outils facilitent la diffusion d'informations et le partage de documents.",
    content2: "Cependant, leur usage doit respecter les règles de confidentialité et de protection des données personnelles. Le secret professionnel s'applique également aux communications numériques. Les informations transmises doivent être nécessaires, proportionnées et sécurisées.",
    content3: "Le professionnel choisit le mode de communication adapté au message et au destinataire. Les échanges sensibles privilégient souvent un contact direct plutôt qu'un message électronique."
  },
  {
    id: 80, type: 'content',
    title: "Exercer une veille sur les usages numériques",
    content: "Le numérique évolue rapidement. Exercer une veille permet de rester informé des nouvelles pratiques, des outils émergents et des recommandations en matière de sécurité.",
    content2: "Cette veille concerne également les usages numériques des familles et des enfants. Les questions liées aux écrans, à la protection des données ou à l'éducation numérique peuvent faire l'objet d'échanges et de sensibilisation.",
    content3: "La veille professionnelle contribue à maintenir une pratique actualisée et responsable. Elle permet d'anticiper les évolutions et d'adapter les pratiques institutionnelles."
  },
  {
    id: 81, type: 'content',
    title: "Synthèse",
    content: "Appréhender et mobiliser l'environnement numérique exige compétence technique, vigilance éthique et capacité d'adaptation. En utilisant les outils de manière réfléchie et en respectant le cadre réglementaire, le professionnel renforce l'efficacité de la communication et la qualité du suivi éducatif. Le numérique devient ainsi un levier au service du projet institutionnel et de la relation avec les familles, tout en préservant la confidentialité et le respect des personnes."
  },
  // SYNTHÈSE DC3
  {
    id: 82, type: 'summary',
    title: "Synthèse DC3",
    points: [
      "Le travail d'équipe est une construction permanente",
      "Différencier et articuler les niveaux de projet",
      "La méthodologie de projet structure l'action",
      "Les réunions sont des outils de coordination essentiels",
      "Les écrits engagent la responsabilité professionnelle",
      "L'EJE est garant de la cohérence éducative"
    ],
    authors: "Cadre réglementaire, CASF, RGPD, Référentiel DEEJE"
  },
  // FÉLICITATIONS
  {
    id: 83, type: 'congrats',
    title: "Félicitations !",
    message: "Vous avez terminé le cours DC3 – Communication professionnelle. Vous maîtrisez désormais les fondamentaux de la communication en travail social, de la conduite de projet à la rédaction d'écrits professionnels, en passant par le travail d'équipe et l'environnement numérique.",
    cta: "Testez vos connaissances avec le Quiz DC3 !"
  },
];

const DEEJEDC3Presentation = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [viewedSlides, setViewedSlides] = useState(new Set([0]));
  const audioRef = useRef(null);
  const API_URL = window.location.origin || '';

  useEffect(() => {
    const saved = getActivityProgress(ACTIVITY_ID);
    if (saved && saved.lastViewedSlide > 0) {
      setCurrentSlide(saved.lastViewedSlide);
      setViewedSlides(new Set(saved.viewedSlides || [0]));
      toast.info(`Reprise à la slide ${saved.lastViewedSlide + 1} / ${slides.length}`);
    }
    markSlideViewed(ACTIVITY_ID, 0, slides.length);
  }, []);

  const transformTextForTTS = (text) => {
    if (!text) return '';
    return text
      .replace(/\bDEEJE\b/g, "diplome d'etat d'educateur de jeunes enfants")
      .replace(/\bEJE\b/g, "educateur de jeunes enfants")
      .replace(/\bDC3\b/g, "domaine de competences 3")
      .replace(/\bCASF\b/g, "code de l'action sociale et des familles")
      .replace(/\bRGPD\b/g, "reglement general sur la protection des donnees")
      .replace(/\bVAE\b/g, "validation des acquis de l'experience");
  };

  const speakText = async (text) => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const transformedText = transformTextForTTS(text).substring(0, 800);
      const response = await fetch(`${API_URL}/api/tts/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: transformedText, voice: 'nova', speed: 1.1 })
      });
      if (response.ok) {
        const data = await response.json();
        if (data.audio_url || data.audio_base64) {
          if (audioRef.current) audioRef.current.pause();
          audioRef.current = new Audio(data.audio_url || `data:audio/mp3;base64,${data.audio_base64}`);
          audioRef.current.play();
          setIsPlaying(true);
          audioRef.current.onended = () => setIsPlaying(false);
        }
      }
    } catch (error) {
      toast.error("Erreur de connexion au service audio");
    } finally {
      setIsLoading(false);
    }
  };

  const stopAudio = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; setIsPlaying(false); }
  };

  const getSlideText = (slide) => {
    let text = slide.title || '';
    if (slide.subtitle) text += '. ' + slide.subtitle;
    if (slide.content) text += '. ' + slide.content;
    if (slide.bullets) text += '. ' + slide.bullets.join('. ');
    if (slide.note) text += '. ' + slide.note;
    return text;
  };

  const nextSlide = () => { if (currentSlide < slides.length - 1) { const next = currentSlide + 1; setCurrentSlide(next); setViewedSlides(prev => new Set([...prev, next])); markSlideViewed(ACTIVITY_ID, next, slides.length); } };
  const prevSlide = () => { if (currentSlide > 0) setCurrentSlide(currentSlide - 1); };
  const goToSlide = (index) => { setCurrentSlide(index); setViewedSlides(prev => new Set([...prev, index])); markSlideViewed(ACTIVITY_ID, index, slides.length); };

  const progressPercent = Math.round((viewedSlides.size / slides.length) * 100);

  const renderSlide = (slide) => {
    switch (slide.type) {
      case 'title':
        return (
          <div className="flex flex-col items-center justify-center text-center relative overflow-hidden" style={{ backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center', minHeight: '430px' }}>
            <div className="absolute inset-0" style={{ background: 'rgba(69,123,157,0.60)' }} />
            <div className="relative z-10 flex flex-col items-center justify-center px-8">
              <h1 className="text-3xl md:text-5xl font-bold text-white mb-6 drop-shadow-lg">{slide.title}</h1>
              <div className="space-y-2">
                <p className="text-lg md:text-xl font-medium drop-shadow" style={{ color: 'rgba(255,255,255,0.70)' }}>{slide.bloc5}</p>
                <p className="text-lg md:text-xl font-medium drop-shadow" style={{ color: 'rgba(255,255,255,0.70)' }}>{slide.bloc6}</p>
              </div>
            </div>
          </div>
        );
      case 'sommaire':
        return (
          <div className="h-full flex flex-col relative" style={{ backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
            <div className="absolute inset-0" style={{ background: 'rgba(245,249,252,0.80)' }} />
            <div className="relative z-10 h-full flex flex-col">
              <div style={{ background: `linear-gradient(135deg, ${theme.primary} 0%, #1D3557 100%)`, padding: '0.5rem 0' }}>
                <h2 className="text-xl font-bold text-center text-white tracking-widest uppercase">Sommaire</h2>
              </div>
              <div className="flex-1 overflow-auto px-3 py-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5 max-w-5xl mx-auto">
                  {slide.sections.map((s, idx) => (
                    <div key={idx}
                      className="flex items-start gap-2 p-2 bg-white/90 rounded-lg shadow-sm cursor-pointer hover:shadow-md hover:scale-[1.01] transition-all backdrop-blur-sm"
                      style={{ border: `1px solid ${theme.primary}20` }}
                      onClick={() => goToSlide(Math.min(idx * 2 + 2, slides.length - 1))}>
                      <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 text-white mt-0.5" style={{ background: theme.primary }}>{s.num}</span>
                      <span className="text-gray-700 text-xs leading-tight font-medium">{s.title}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      case 'section':
        return (
          <div className="flex flex-col items-center justify-center relative overflow-hidden" style={{ backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center', minHeight: '430px' }}>
            <div className="absolute inset-0" style={{ background: 'rgba(29,53,87,0.65)' }} />
            <h2 className="text-2xl md:text-3xl font-bold text-white text-center px-10 relative z-10 drop-shadow-lg whitespace-pre-line">{slide.title}</h2>
          </div>
        );
      case 'content':
        return (
          <div className="p-6 overflow-auto flex flex-col items-center justify-center" style={{ background: theme.bg, minHeight: '430px' }}>
            <div className="max-w-3xl mx-auto w-full">
              <h2 className="text-2xl font-bold mb-4 text-center" style={{ color: theme.primary }}>{slide.title}</h2>
              <p className="text-gray-700 mb-3 leading-relaxed text-base text-center">{slide.content}</p>
              {slide.content2 && <p className="text-gray-700 mb-3 leading-relaxed text-base text-center">{slide.content2}</p>}
              {slide.content3 && <p className="text-gray-700 mb-3 leading-relaxed text-base text-center">{slide.content3}</p>}
              {slide.bullets && (
                <div className="space-y-2 mb-3">
                  {slide.bullets.map((b, i) => (
                    <div key={i} className="flex items-start gap-2 bg-white p-2.5 rounded-lg shadow-sm">
                      <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ background: theme.primary }} />
                      <span className="text-gray-700 text-base">{b}</span>
                    </div>
                  ))}
                </div>
              )}
              {slide.note && (
                <div className="p-3 rounded-lg text-base italic text-center" style={{ background: theme.primary + '15', borderLeft: `3px solid ${theme.primary}` }}>
                  {slide.note}
                </div>
              )}
            </div>
          </div>
        );
      case 'competence':
        return (
          <div className="p-6 overflow-auto flex flex-col items-center justify-center" style={{ background: theme.bg, minHeight: '430px' }}>
            <div className="max-w-3xl mx-auto w-full">
              <div className="p-3 rounded-t-lg text-white font-bold text-center text-lg" style={{ background: theme.primary }}>
                {slide.title}
              </div>
              <div className="bg-white rounded-b-lg shadow-md p-4 space-y-2">
                {slide.items.map((item, i) => (
                  <div key={i} className="flex items-start gap-2 p-2.5 border-b last:border-0" style={{ borderColor: theme.primary + '20' }}>
                    <Sparkles size={16} className="mt-0.5 flex-shrink-0" style={{ color: theme.secondary }} />
                    <span className="text-gray-700 text-base">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      case 'summary':
        return (
          <div className="flex flex-col items-center justify-center p-8 relative overflow-hidden" style={{ backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center', minHeight: '430px' }}>
            <div className="absolute inset-0" style={{ background: 'rgba(69,123,157,0.60)' }} />
            <div className="relative z-10 flex flex-col items-center">
              <h2 className="text-2xl font-bold text-white mb-4 text-center drop-shadow-lg">{slide.title}</h2>
              <div className="space-y-2 max-w-xl w-full mb-4">
                {slide.points.map((p, i) => (
                  <div key={i} className="flex items-center gap-2 p-2.5 rounded-lg" style={{ background: 'rgba(255,255,255,0.15)' }}>
                    <FileText size={16} className="text-white flex-shrink-0" />
                    <span className="text-white text-base">{p}</span>
                  </div>
                ))}
              </div>
              <div className="p-3 rounded-lg max-w-xl w-full" style={{ background: 'rgba(255,255,255,0.9)' }}>
                <p className="text-sm text-center" style={{ color: theme.primary }}>
                  <strong>Références :</strong> {slide.authors}
                </p>
              </div>
            </div>
          </div>
        );
      case 'congrats':
        return (
          <div className="flex flex-col items-center justify-center relative overflow-hidden" style={{ backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center', minHeight: '430px' }}>
            <div className="absolute inset-0" style={{ background: 'rgba(69,123,157,0.65)' }} />
            <div className="relative z-10 flex flex-col items-center text-center px-8">
              <div className="text-6xl mb-4">&#127891;</div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 drop-shadow-lg">{slide.title}</h2>
              <p className="text-white text-lg leading-relaxed max-w-2xl mb-6">{slide.message}</p>
              <div className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.9)' }}>
                <p className="text-lg font-semibold" style={{ color: theme.primary }}>{slide.cta}</p>
              </div>
            </div>
          </div>
        );
      default: return null;
    }
  };

  const slide = slides[currentSlide];
  return (
    <div className="rounded-xl overflow-hidden shadow-lg" style={{ background: theme.bg }} data-testid="deeje-dc3-presentation">
      <div>{renderSlide(slide)}</div>
      <div className="w-full h-2 bg-gray-200">
        <div className="h-full rounded-r-full transition-all duration-300" style={{ width: `${progressPercent}%`, background: `linear-gradient(90deg, ${theme.primary}, ${theme.secondary})` }} />
      </div>
      <div className="flex items-center justify-between p-3 bg-white border-t" style={{ borderColor: theme.primary + '30' }}>
        <button onClick={prevSlide} disabled={currentSlide === 0} className="flex items-center gap-1 px-3 py-2 rounded-lg border text-sm font-medium disabled:opacity-40" style={{ borderColor: theme.primary, color: theme.text }} data-testid="dc3-prev-btn">
          <ChevronLeft size={16} /> Précédent
        </button>
        <div className="flex items-center gap-2">
          <button onClick={() => isPlaying ? stopAudio() : speakText(getSlideText(slide))} className="w-9 h-9 rounded-full flex items-center justify-center text-white" style={{ background: theme.primary }} data-testid="dc3-audio-btn">
            {isLoading ? <Loader2 size={16} className="animate-spin" /> : isPlaying ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </button>
          <span className="font-bold text-sm" style={{ color: theme.primary }}>{currentSlide + 1} / {slides.length}</span>
          <span className="text-xs text-gray-400">{progressPercent}%</span>
        </div>
        <button onClick={nextSlide} disabled={currentSlide === slides.length - 1} className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-40" style={{ background: theme.primary }} data-testid="dc3-next-btn">
          Suivant <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
};

export default DEEJEDC3Presentation;
